// @flow
import React from 'react';
import cx from 'classnames';
import dataFormat from 'date-fns/format';
import Button from 'components/common/Button';
import {
  imageURL,
  parseArrayString,
  orderShippingStatus,
  translationKeys,
  numberWithCommas,
} from 'lib/common';
import { Link } from 'react-router-dom';
import { camelCase, kebabCase, get } from 'lodash';
import { generateShippingToken } from 'lib/shippingToken';
import { MdInfo, MdWarning, MdCheckCircle } from 'react-icons/md';
import { track } from 'lib/tracking';
import { TRACKING_EVENTS } from 'lib/trackingEvents';
import './OrderDetailTemplate.scss';

type Props = {
  order: any,
  goProduct: any,
  goInvoice: any,
  goOffer: any,
  goTax: any,
  goRefund: any,
  goNicePayReceipt: any,
  goTossPayReceipt: any,
  goPayJoaReceipt: any,
  goBoltaTax: any,
  onCheckOut: any,
  onCancelOrder: any,
  onPaymentMethod: any,
  cancelOrder: boolean,
  handleChange: any,
  onCopyPurchaserLink: any,
};

const bankCodes = {
  '39': '경남은행',
  '34': '광주은행',
  '12': '단위농협(지역농축협)',
  '32': '부산은행',
  '45': '새마을금고',
  '64': '산림조합',
  '88': '신한은행',
  '48': '신협',
  '27': '씨티은행',
  '20': '우리은행',
  '71': '우체국예금보험',
  '50': '저축은행중앙회',
  '37': '전북은행',
  '35': '제주은행',
  '90': '카카오뱅크',
  '89': '케이뱅크',
  '92': '토스뱅크',
  '81': '하나은행',
  '54': '홍콩상하이은행',
  '03': 'IBK기업은행',
  '04': 'KB국민은행',
  '31': 'iM뱅크(대구)',
  '02': '한국산업은행',
  '11': 'NH농협은행',
  '23': 'SC제일은행',
  '07': 'Sh수협은행',
};

function getBankName(code) {
  return bankCodes[code] || '알 수 없는 코드';
}

const statusTrans = key => {
  let words = {
    statusPlaced: '결제 대기',
    statusCancelledByStore: '운영자 취소',
    statusCancelledByCustomer: '고객 취소',
    statusPaid: '결제 완료',
    statusUnderPaid: '부분 결제',
    statusOverPaid: '초과 결제',
    statusRefunded: '환불 완료',
    statusPartiallyRefunded: '부분 환불 완료',
    statusUnderRefunded: '환불 처리 중',
    statusOverRefunded: '초과 환불',
    shippingStatusPending: '배송 대기',
    shippingStatusShipped: '배송중',
    shippingStatusArrived: '배송 완료',
    shippingStatusReceived: '수령 완료',
    refundStatusRequested: '승인 대기',
    refundStatusCancelledByStore: '운영자 취소',
    refundStatusCancelledByCustomer: '고객 취소',
    refundStatusAccepted: '환불 승인',
    orderTotalPaid: '결제 금액',
    orderTotalCancelled: '결제 취소 금액',
    orderTotalRefunded: '그 외 환불 금액',
    customerFirstName: '주문자 이름',
    customerLastName: '주문자 성',
    customerFullName: '주문자',
    customerEmail: '이메일',
    customerMobile: '연락처',
    customerPhone: '사업자등록번호',
    orderShippingAddress: '배송지 주소',
    addressFirstName: '주문자 이름',
    addressLastName: '주문자 성',
    addressFullName: '받는분',
    addressMobile: '연락처',
    addressPhone: '사업자등록번호',
    fulfillmentStatusPending: '배송 대기',
    fulfillmentStatusShipped: '배송중',
    fulfillmentStatusArrived: '배송 완료',
    // naverPay:                        '네이버페이',
    niceByCard: '신용카드',
    virtualBank: '가상계좌',
  };
  return words[key];
};

const customerFields = parseArrayString(
  ['name.full:required', 'mobile:required', 'email:required', 'phone'].map(
    field => field.split(':')[0],
  ),
).map(key => ({
  key,
  translationKey: camelCase(['customer', translationKeys[key] || key]),
}));

const recipientFields = parseArrayString(
  ['name.full:required', 'mobile:required'].map(field => field.split(':')[0]),
).map(key => ({
  key,
  translationKey: camelCase(['address', translationKeys[key] || key]),
}));

const refundTotalDetail = total =>
  [
    typeof total.items.price.withTax.raw === 'number'
      ? `상품 환불액: ${total.items.price.withTax.raw}원`
      : null,
    typeof total.shipping.fee.withTax.raw === 'number'
      ? `배송비 환불액: ${total.shipping.fee.withTax.raw}원`
      : null,
  ]
    .filter(v => v)
    .join(' + ');

const hasTangibleItem = allItems => allItems.some(item => item.type === 'tangible');

const fulfillmentTracker = ({ tracking }) => {
  const message = [tracking.company, tracking.uid ? `(${tracking.uid})` : null]
    .filter(v => v)
    .join(' ');

  return tracking.url ? (
    <div>
      <a href={tracking.url} target="_blank">
        {message || '배송 조회'}
      </a>
    </div>
  ) : (
    <div>
      <span>{message}</span>
    </div>
  );
};

const paymentMethods = [
  // {
  //   id: 'naverpay',
  //   label: 'naverPay',
  //   value: '네이버페이',
  //   cardFields: false,
  //   payLater: false,
  // },
  {
    id: 'tosspayments',
    label: 'tosspayments',
    cardFields: false,
    payLater: false,
    meta: {
      pg: 'tosspayments',
      payMethod: 'card',
    },
  },
  {
    id: 'iamport',
    label: 'niceByCard',
    cardFields: false,
    payLater: false,
    meta: {
      pg: 'nice',
      payMethod: 'card',
    },
  },
  {
    id: 'virtual-bank',
    label: 'virtualBank',
    cardFields: false,
    payLater: false,
    meta: {
      pg: 'nice',
      payMethod: 'vbank',
    },
  },
];

const paymentMethodValue = [
  {
    id: 'naverpay',
    label: 'naverPay',
    value: '네이버페이',
    cardFields: false,
    payLater: false,
  },
  {
    id: 'tosspay',
    label: 'tossPay',
    value: '토스페이',
    cardFields: false,
    payLater: false,
  },
  {
    id: 'kakaopay',
    label: 'kakaoPay',
    value: '카카오페이',
    cardFields: false,
    payLater: false,
  },
  {
    id: 'tosspayments',
    label: 'tosspayments',
    value: '신용카드',
    cardFields: false,
    payLater: false,
    meta: {
      pg: 'tosspayments',
      payMethod: 'card',
    },
  },
  {
    id: 'iamport',
    label: 'niceByCard',
    value: '신용카드',
    cardFields: false,
    payLater: false,
    meta: {
      pg: 'nice',
      payMethod: 'card',
    },
  },
  {
    id: 'clayful-iamport',
    label: 'niceByCard',
    value: '신용카드',
    cardFields: false,
    payLater: false,
    meta: {
      pg: 'nice',
      payMethod: 'card',
    },
  },
  {
    id: 'virtual-bank',
    label: 'virtualBank',
    value: '가상계좌',
    cardFields: false,
    payLater: false,
    meta: {
      pg: 'nice',
      payMethod: 'vbank',
    },
  },
  {
    id: 'research-transfer',
    label: 'researchTransfer',
    value: '연구비카드',
    cardFields: false,
    payLater: true,
  },
  {
    id: 'kb-bank-transfer',
    label: 'kbBankTransfer',
    value: '세금계산서 (신한은행 100-032-703829)',
    cardFields: false,
    payLater: true,
  },
  {
    id: 'bank-transfer',
    label: 'bankTransfer',
    value: '세금계산서 (기업은행 033-502993-01-019)',
    cardFields: false,
    payLater: true,
  },
  {
    id: 'post-transfer',
    label: 'postTransfer',
    value: '후불결제',
    cardFields: false,
    payLater: true,
  },
  {
    id: 'quick-bank-transfer',
    label: 'quickBankTransfer',
    value: '퀵계좌이체',
    cardFields: false,
    payLater: true,
  },
  {
    id: 'inventory-transfer',
    label: 'inventoryTransfer',
    value: '재고 확인 후 결제',
    cardFields: false,
    payLater: true,
  },
  {
    id: 'default',
    label: 'default',
    value: '디폴트',
    cardFields: false,
    payLater: true,
  },
];

const OrderDetailTemplate = (props: Props) => (
  <div className="OrderDetailTemplate">
    <div className="order-detail-container">
      {/* 헤더 섹션 */}
      <div className="order-detail-header">
        <div className="header-content">
          <div className="header-text">
            <h1 className="header-title">
              <button
                className="back-button"
                onClick={() => window.history.back()}
                aria-label="뒤로가기"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M19 12H5M12 19l-7-7 7-7" />
                </svg>
              </button>
              주문 상세 내역
            </h1>
          </div>
        </div>
      </div>

      <div className="order-detail" data-order={props.order.id}>
        <div className="order-primary-info">
          <div className="order-basic-info card">
            <div className="card-header">
              <h3 className="card-title">
                <span aria-label="정보"></span>
                기본 정보
              </h3>
            </div>
            <div className="card-content">
              <div className="order-status-container">
                <span className="tag order-status" data-status={props.order.status}>
                  {props.order.status === 'cancelled'
                    ? statusTrans(
                        camelCase([
                          'status',
                          props.order.status,
                          'by',
                          props.order.cancellation.by,
                        ]),
                      )
                    : statusTrans(camelCase(['status', props.order.status]))}
                </span>
                {orderShippingStatus(props.order) && (
                  <span
                    className="tag order-shipping-status"
                    data-status={orderShippingStatus(props.order)}
                  >
                    {statusTrans(camelCase(['shippingStatus', orderShippingStatus(props.order)]))}
                  </span>
                )}
              </div>

              {!props.order.synced && (
                <p className="order-sync-failed">주문에 실패했습니다. 관리자에게 문의해주세요.</p>
              )}

              <table>
                <tbody>
                  <tr className="order-id">
                    <th className="data-label">주문일시</th>
                    <td className="data-value">
                      {dataFormat(props.order.created_at, 'YYYY년 M월 D일 HH시 mm분')}
                    </td>
                  </tr>
                  <tr className="order-id">
                    <th className="data-label">주문번호</th>
                    <td className="data-value">{props.order.id}</td>
                  </tr>
                  {props.order.transactions[0] && props.order.transactions[0].paymentMethod.slug && (
                    <tr className="order-id">
                      <th className="data-label">결제수단</th>
                      <td className="data-value">
                        {paymentMethodValue.find(
                          doc => doc.id === props.order.transactions[0].paymentMethod.slug,
                        ).value || '없음'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {props.order.cancellation && props.order.cancellation.reason && (
                <div className="order-cancellation">
                  <h4>취소 내용</h4>
                  <div className="order-cancellation-reason">{props.order.cancellation.reason}</div>
                </div>
              )}

              {props.order.request && (
                <div className="order-request">
                  <h4>요청 사항</h4>
                  <div className="order-request-body">{props.order.request}</div>
                </div>
              )}

              {!!props.order.transactions.length && (
                <div className="order-transaction-summary">
                  <h4>결제 정보</h4>
                  <table className="order-transaction-details">
                    <tbody>
                      {['paid', 'cancelled', 'refunded'].map(key => (
                        <tr key={key} className={`total-${key}`}>
                          <th className="data-label">
                            {statusTrans(camelCase(['orderTotal', key]))}
                          </th>
                          <td className="data-value">
                            {numberWithCommas(props.order.transactions[0][key].raw)}원
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {!!props.order.tosspayments && !!props.order.tosspayments.virtualAccount && (
                <div className="order-vbanks">
                  <div className="section-header">
                    <h3 className="section-title">가상 계좌 정보</h3>
                    <p className="section-description">입금을 위한 가상 계좌 정보입니다.</p>
                  </div>
                  <table className="vbank-info">
                    <tbody>
                      <tr className="vbank-name">
                        <th className="data-label">은행</th>
                        <td className="data-value">
                          {getBankName(props.order.tosspayments.virtualAccount.bankCode)}
                        </td>
                      </tr>
                      <tr className="vbank-number">
                        <th className="data-label">계좌번호</th>
                        <td className="data-value">
                          {props.order.tosspayments.virtualAccount.accountNumber}
                        </td>
                      </tr>
                      <tr className="vbank-amount">
                        <th className="data-label">입금 금액</th>
                        <td className="data-value">
                          {numberWithCommas(props.order.tosspayments.totalAmount)}원
                        </td>
                      </tr>
                      <tr className="vbank-expires-at">
                        <th className="data-label">입금 기한</th>
                        <td className="data-value">
                          {dataFormat(
                            props.order.tosspayments.virtualAccount.dueDate,
                            'YYYY년 M월 D일 HH시 mm분',
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {!!props.order.transactions.length && !!props.order.transactions[0].vbanks.length && (
                <div className="order-vbanks">
                  <div className="section-header">
                    <h3 className="section-title">가상 계좌 정보</h3>
                    <p className="section-description">입금을 위한 가상 계좌 정보입니다.</p>
                  </div>
                  <table className="vbank-info">
                    <tbody>
                      <tr className="vbank-name">
                        <th className="data-label">은행</th>
                        <td className="data-value">{props.order.transactions[0].vbanks[0].name}</td>
                      </tr>
                      <tr className="vbank-number">
                        <th className="data-label">계좌번호</th>
                        <td className="data-value">
                          {props.order.transactions[0].vbanks[0].number}
                        </td>
                      </tr>
                      <tr className="vbank-amount">
                        <th className="data-label">입금 금액</th>
                        <td className="data-value">
                          {props.order.transactions[0].vbanks[0].amount.converted}
                        </td>
                      </tr>
                      <tr className="vbank-expires-at">
                        <th className="data-label">입금 기한</th>
                        <td className="data-value">
                          {props.order.transactions[0].vbanks[0].expiresAt.formatted}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {props.order.status === 'placed' && (
                <div className="cancel-action-container">
                  <button className="cancel-button" onClick={props.onCancelOrder}>
                    주문 취소하기
                  </button>
                </div>
              )}

              {props.order.status !== 'cancelled' && props.cancelOrder && (
                <div className="cancellation-form-container">
                  <div className="section-header">
                    <h3 className="section-title">취소 사유 입력</h3>
                    <p className="section-description">
                      주문 취소를 위해 취소 사유를 입력해주세요.
                    </p>
                  </div>
                  <form className="cancellation-form">
                    <textarea
                      name="reason"
                      value={props.reason}
                      className="order-cancellation-reason"
                      placeholder="취소 사유를 입력해주세요"
                      onChange={props.handleChange}
                      rows="4"
                    />
                    <button type="button" className="cancel-submit-button" onClick={props.onCancel}>
                      취소 완료
                    </button>
                  </form>
                </div>
              )}
            </div>
          </div>

          <div className="order-item-details card">
            <div className="card-header">
              <h3 className="card-title">
                <span aria-label="상품"></span>
                주문 품목
              </h3>
            </div>
            <div className="card-content">
              <div className="order-items-list">
                {props.order.items.map((item, index) => (
                  <div key={item.id} className="order-item-card" data-item={item.id}>
                    {/* 첫번째 줄: 상품명 (타이틀) */}
                    <div className="order-item-title">
                      {item.product ? (
                        <a
                          onClick={() => props.goProduct(item.product.id)}
                          className="order-item-name-link"
                        >
                          {item.product.name}
                        </a>
                      ) : (
                        '삭제된 상품'
                      )}
                    </div>

                    {/* 두번째 줄: 카탈로그 번호, 단위 */}
                    <div className="order-item-meta">
                      {item.product_price && (
                        <span className="order-item-catalog">
                          <span className="meta-label">카탈로그 번호:</span>
                          <span className="meta-value">{item.product_price.part_number}</span>
                        </span>
                      )}
                      {item.product_price && (
                        <span className="order-item-unit">
                          <span className="meta-label">단위:</span>
                          <span className="meta-value">{item.product_price.unit}</span>
                        </span>
                      )}
                    </div>

                    {/* 세번째 줄: 이미지 | 설명 | 수량 (가로 배치) */}
                    <div className="order-item-details">
                      <div className="order-item-image">
                        <a onClick={() => props.goProduct(item.product.id)}>
                          <img
                            src={item.product ? imageURL(item.product.thumbnail, 80, 80) : ''}
                            className="item-thumbnail"
                            alt="상품 이미지"
                          />
                        </a>
                      </div>
                      <div className="order-item-description">
                        {item.product_price && item.product_price.description}
                      </div>
                      <div className="order-item-quantity">
                        <span className="quantity-label">수량:</span>
                        <span className="quantity-value">{item.quantity}</span>
                      </div>
                    </div>

                    {/* 네번째 줄: 단가 | 가격 (가로 배치) */}
                    <div className="order-item-pricing">
                      <div className="order-item-unit-price">
                        <span className="price-label">단가:</span>
                        {item.price ? (
                          <div className="price-value">
                            {item.price.sale.raw !== item.price.original.raw && (
                              <span className="original-price">
                                {numberWithCommas(item.price.original.raw)}원
                              </span>
                            )}
                            <span className="sale-price">
                              {numberWithCommas(item.price.sale.raw)}원
                            </span>
                          </div>
                        ) : (
                          <div className="price-value">
                            <span className="sale-price">
                              {numberWithCommas(item.product_price.price)}원
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="order-item-total-price">
                        <span className="price-label">가격:</span>
                        <div className="price-value">
                          {item.total.price.sale.raw !== item.total.price.original.raw && (
                            <div className="discount-info">
                              <span className="original-price">
                                {numberWithCommas(item.total.price.original.raw)}원
                              </span>
                            </div>
                          )}
                          <span className="sale-price">
                            {numberWithCommas(item.total.price.sale.raw)}원
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* 쿠폰 정보 */}
              {props.order.appliedCoupon && (
                <div className="order-applied-coupon">
                  <span className="coupon-label">적용 쿠폰:</span>
                  <span className="coupon-name">{props.order.appliedCoupon.name}</span>
                  <span
                    data-balloon-break
                    data-balloon-pos="up"
                    data-balloon={props.couponDetail(props.order.appliedCoupon)}
                    className="coupon-info"
                  >
                    <span className="question-mark"></span>
                  </span>
                </div>
              )}

              {/* 주문 총계 정보 */}
              <div className="order-totals">
                <div className="total-row item-total">
                  <div className="total-label">상품 합계</div>
                  <div className="total-value">
                    <span className="sale-price">
                      {numberWithCommas(props.order.total.price.sale.raw)}원
                    </span>
                    {props.order.total.price.sale.raw !== props.order.total.price.original.raw && (
                      <div className="discount-info">
                        <span className="original-price">
                          {numberWithCommas(props.order.total.price.original.raw)}원
                        </span>
                        <span className="discount-amount">
                          (-{numberWithCommas(props.order.total.discounted.raw)}원)
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="total-row shipping-total">
                  <div className="total-label">배송비 합계</div>
                  <div className="total-value">
                    {numberWithCommas(props.order.total.shipping.fee.sale.raw)}원
                  </div>
                </div>
                <div className="total-row tax-total">
                  <div className="total-label">부가세</div>
                  <div className="total-value">
                    {numberWithCommas(props.order.total.taxed.raw)}원
                  </div>
                </div>
                <div className="total-row order-total">
                  <div className="total-label">총계</div>
                  <div className="total-value">
                    {numberWithCommas(props.order.total.amount.raw)}원
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 결제수단 카드 */}
        {props.order.status === 'placed' &&
          (props.order.transactions[0].paymentMethod.slug === 'bank-transfer' ||
            props.order.transactions[0].paymentMethod.slug === 'post-transfer' ||
            props.order.transactions[0].paymentMethod.slug === 'virtual-bank') && (
            <div className="order-payment-method card">
              <div className="card-header">
                <h3 className="card-title">결제수단</h3>
                <button
                  className="purchaser-link-button-header"
                  onClick={() => props.onCopyPurchaserLink(props.order.id)}
                >
                  구매담당자 링크 복사
                </button>
              </div>
              <div className="card-content">
                <div className="payment-widget-container">
                  <div id="payment-widget" />
                </div>
                <div className="payment-action">
                  <button className="payment-button" onClick={props.onCheckOut}>
                    결제하기
                  </button>
                </div>
              </div>
            </div>
          )}

        {/* 증빙서류 카드 */}
        <div className="order-documents card">
          <div className="card-header">
            <h3 className="card-title">증빙서류</h3>
          </div>
          <div className="card-content">
            <div className="document-grid">
              <button
                className="document-button document-business-cert"
                onClick={() =>
                  props.downloadDocument(
                    'https://images.cacheby.com/documents/데이터씨_사업자등록증.pdf',
                    '데이터씨_사업자등록증.pdf',
                  )
                }
              >
                <div className="button-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div className="button-content">
                  <span className="button-title">사업자등록증</span>
                  <span className="button-description">Business Registration</span>
                </div>
              </button>

              <button
                className="document-button document-bank-copy"
                onClick={() =>
                  props.downloadDocument(
                    'https://images.cacheby.com/documents/데이터씨_통장사본.pdf',
                    '데이터씨_통장사본.pdf',
                  )
                }
              >
                <div className="button-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                </div>
                <div className="button-content">
                  <span className="button-title">통장사본</span>
                  <span className="button-description">Bank Account Copy</span>
                </div>
              </button>

              <button
                className="document-button document-estimate"
                onClick={() => props.goOffer(props.order.id)}
              >
                <div className="button-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                </div>
                <div className="button-content">
                  <span className="button-title">견적서</span>
                  <span className="button-description">Estimate</span>
                </div>
              </button>

              <button
                className="document-button document-invoice"
                onClick={() => props.goInvoice(props.order.id)}
              >
                <div className="button-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div className="button-content">
                  <span className="button-title">거래명세서</span>
                  <span className="button-description">Invoice</span>
                </div>
              </button>

              {!!props.order.bolta &&
                !!props.order.bolta.data &&
                !!props.order.bolta.data.taxInvoiceUrl && (
                  <button className="document-button document-tax" onClick={props.goBoltaTax}>
                    <div className="button-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="button-content">
                      <span className="button-title">세금계산서</span>
                      <span className="button-description">Tax Invoice</span>
                    </div>
                  </button>
                )}

              {!props.order.tosspayments &&
                (props.order.transactions[0].paymentMethod.slug === 'iamport' ||
                  props.order.transactions[0].paymentMethod.slug === 'tosspayments') && (
                  <button
                    className="document-button document-receipt"
                    onClick={props.goNicePayReceipt}
                  >
                    <div className="button-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
                      </svg>
                    </div>
                    <div className="button-content">
                      <span className="button-title">영수증</span>
                      <span className="button-description">Receipt</span>
                    </div>
                  </button>
                )}

              {!props.order.tosspayments &&
                props.order.transactions[0].paymentMethod.slug === 'clayful-iamport' && (
                  <button
                    className="document-button document-receipt"
                    onClick={props.goNicePayReceipt}
                  >
                    <div className="button-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
                      </svg>
                    </div>
                    <div className="button-content">
                      <span className="button-title">영수증</span>
                      <span className="button-description">Receipt</span>
                    </div>
                  </button>
                )}

              {!props.order.tosspayments &&
                props.order.transactions[0].paymentMethod.slug === 'research-transfer' && (
                  <button
                    className="document-button document-receipt"
                    onClick={props.goPayJoaReceipt}
                  >
                    <div className="button-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
                      </svg>
                    </div>
                    <div className="button-content">
                      <span className="button-title">영수증</span>
                      <span className="button-description">Receipt</span>
                    </div>
                  </button>
                )}

              {!!props.order.tosspayments && props.order.transactions[0].paymentMethod.slug && (
                <button
                  className="document-button document-receipt"
                  onClick={props.goTossPayReceipt}
                >
                  <div className="button-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
                    </svg>
                  </div>
                  <div className="button-content">
                    <span className="button-title">영수증</span>
                    <span className="button-description">Receipt</span>
                  </div>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* 자료 다운로드 카드 (MSDS/기타) */}
        {props.order.meta && props.order.meta.documents && props.order.meta.documents.length > 0 && (
          <div className="order-download-documents card">
            <div className="card-header">
              <h3 className="card-title">자료 다운로드</h3>
            </div>
            <div className="card-content">
              <table className="download-documents-table">
                <thead>
                  <tr>
                    <th>유형</th>
                    <th>파일명</th>
                    <th>업로드 일시</th>
                  </tr>
                </thead>
                <tbody>
                  {props.order.meta.documents.map((doc, index) => (
                    <tr key={doc.id || index}>
                      <td>{doc.type === 'msds' ? 'MSDS' : '기타'}</td>
                      <td>
                        <a href={doc.url} target="_blank" rel="noopener noreferrer">
                          {doc.filename}
                        </a>
                      </td>
                      <td>
                        {doc.uploadedAt
                          ? dataFormat(new Date(doc.uploadedAt), 'YYYY-MM-DD HH:mm')
                          : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* 세금계산서 발행 정보 카드 */}
        {props.order.documentaryEvidenceInfo && (
          <div className="order-tax-invoice-info card">
            <div className="card-header">
              <h3 className="card-title">세금계산서 발행 정보</h3>
            </div>
            <div className="card-content">
              {!!props.order.bolta &&
              !!props.order.bolta.data &&
              !!props.order.bolta.data.taxInvoiceUrl ? (
                <div className="tax-invoice-notice tax-invoice-issued">
                  <div className="notice-icon">
                    <MdCheckCircle />
                  </div>
                  <div className="notice-text">
                    세금계산서가 발행되었어요. 이메일 또는 위의 증빙서류 세금계산서 버튼을 클릭하여
                    확인해 주세요.
                  </div>
                </div>
              ) : (
                <div className="tax-invoice-notice">
                  <div className="notice-icon">
                    <MdInfo />
                  </div>
                  <div className="notice-text">
                    세금계산서는 배송완료 후 1~2일 이내 기입해주신 이메일로 전자발행됩니다.
                  </div>
                </div>
              )}

              <div className="tax-invoice-warning">
                <div className="warning-icon">
                  <MdWarning />
                </div>
                <div className="warning-text">
                  발행일 기준 익월 10일 이후에는 세금계산서 수정발급이 불가합니다.
                  <br />
                  발급된 영수증의 내용 변경을 원할 경우, 변경할 사업자 등록증과 함께 실시간 문의로
                  요청해 주세요.
                </div>
                <button
                  className="inquiry-button"
                  onClick={() => {
                    if (window.Tawk_API) {
                      window.Tawk_API.toggle();
                      track(TRACKING_EVENTS.CHAT_WIDGET_OPENED, {
                        context: 'order_detail',
                        orderId: props.order && props.order.id,
                      });
                    }
                  }}
                >
                  실시간 문의
                </button>
              </div>

              <div className="tax-invoice-grid">
                <div className="tax-invoice-section">
                  <div className="tax-invoice-row">
                    <span className="tax-label">담당자명</span>
                    <span className="tax-value">{props.order.documentaryEvidenceInfo.name}</span>
                  </div>
                  <div className="tax-invoice-row">
                    <span className="tax-label">담당자 연락처</span>
                    <span className="tax-value">{props.order.documentaryEvidenceInfo.contact}</span>
                  </div>
                  <div className="tax-invoice-row">
                    <span className="tax-label">상호/법인명</span>
                    <span className="tax-value">
                      {props.order.documentaryEvidenceInfo.companyName}
                    </span>
                  </div>
                  <div className="tax-invoice-row">
                    <span className="tax-label">대표자명</span>
                    <span className="tax-value">
                      {props.order.documentaryEvidenceInfo.representativeName}
                    </span>
                  </div>
                  <div className="tax-invoice-row">
                    <span className="tax-label">업태</span>
                    <span className="tax-value">
                      {props.order.documentaryEvidenceInfo.businessType || '-'}
                    </span>
                  </div>
                </div>

                <div className="tax-invoice-section">
                  <div className="tax-invoice-row">
                    <span className="tax-label">세금계산서 이메일</span>
                    <span className="tax-value">{props.order.documentaryEvidenceInfo.email}</span>
                  </div>
                  <div className="tax-invoice-row">
                    <span className="tax-label">담당자 보조 연락처</span>
                    <span className="tax-value">
                      {props.order.documentaryEvidenceInfo.subContact || '-'}
                    </span>
                  </div>
                  <div className="tax-invoice-row">
                    <span className="tax-label">사업자등록번호</span>
                    <span className="tax-value">
                      {props.order.documentaryEvidenceInfo.businessNo}
                    </span>
                  </div>
                  <div className="tax-invoice-row">
                    <span className="tax-label">사업장 주소</span>
                    <span className="tax-value">
                      {props.order.documentaryEvidenceInfo.businessAddress}
                    </span>
                  </div>
                  <div className="tax-invoice-row">
                    <span className="tax-label">업종</span>
                    <span className="tax-value">
                      {props.order.documentaryEvidenceInfo.businessItem || '-'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 주문자 정보 카드 */}
        <div className="order-customer-info card">
          <div className="card-header">
            <h3 className="card-title">
              <span aria-label="주문자"></span>
              주문자 정보
            </h3>
          </div>
          <div className="card-content">
            <table>
              <tbody>
                {customerFields.map((field, index) => (
                  <tr key={field.key} className={`order-${kebabCase(field.translationKey)}`}>
                    <th className="data-label">{statusTrans(field.translationKey)}</th>
                    <td className="data-value">{get(props.order.customer, field.key) || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* 배송지 정보 카드 */}
        <div className="order-shipping-info card">
          <div className="card-header">
            <h3 className="card-title">
              <span aria-label="배송지"></span>
              배송지 정보
            </h3>
          </div>
          <div className="card-content">
            <table>
              <tbody>
                {recipientFields.map((field, index) => (
                  <tr key={field.key} className={`order-${kebabCase(field.translationKey)}`}>
                    <th className="data-label">{statusTrans(field.translationKey)}</th>
                    <td className="data-value">
                      {get(props.order.address.shipping, field.key) || ''}
                    </td>
                  </tr>
                ))}
                <tr className="address-state">
                  <th className="data-label">도</th>
                  <td className="data-value">{get(props.order.address.shipping, 'state') || ''}</td>
                </tr>
                <tr className="address-city">
                  <th className="data-label">시</th>
                  <td className="data-value">{get(props.order.address.shipping, 'city') || ''}</td>
                </tr>
                <tr className="address-address1">
                  <th className="data-label">주소</th>
                  <td className="data-value">
                    {get(props.order.address.shipping, 'address1') || ''}
                  </td>
                </tr>
                <tr className="address-address2">
                  <th className="data-label">상세주소</th>
                  <td className="data-value">
                    {get(props.order.address.shipping, 'address2') || ''}
                  </td>
                </tr>
                <tr className="address-postcode">
                  <th className="data-label">우편번호</th>
                  <td className="data-value">
                    {get(props.order.address.shipping, 'postcode') || ''}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="order-secondary-info">
          {
            <div className="order-fulfillments card">
              <div className="card-header">
                <h3 className="card-title">
                  <span aria-label="배송"></span>
                  배송 내역
                </h3>
              </div>
              <div className="card-content">
                <ul className="fulfillment-actions">
                  <li>
                    {props.order.receivedAt && (
                      <Button
                        className="button-tiny mark-order-as-not-received"
                        onClick={props.onMarkAsNotReceived}
                      >
                        배송 수령 미완료 처리
                      </Button>
                    )}
                    {!props.order.receivedAt && (
                      <Button
                        className="button-tiny mark-order-as-received"
                        onClick={props.onMarkAsReceived}
                      >
                        배송 수령 완료 처리
                      </Button>
                    )}
                  </li>
                </ul>

                {!props.order.fulfillments.length && (
                  <p className="no-fulfillments">배송 내역이 없습니다.</p>
                )}

                {!!props.order.fulfillments.length && (
                  <table className="table order-fulfillment-list">
                    <thead>
                      <tr>
                        <th>배송 상태</th>
                        <th>배송 품목</th>
                        <th>배송 조회</th>
                      </tr>
                    </thead>

                    <tbody>
                      {props.order.fulfillments.map((fulfillment, index) => (
                        <tr
                          key={fulfillment._id}
                          className="order-fulfillment"
                          data-fulfillment={fulfillment._id}
                        >
                          <td>
                            <span
                              className="tag fulfillment-status"
                              data-status={fulfillment.status}
                            >
                              {statusTrans(camelCase(['fulfillmentStatus', fulfillment.status]))}
                            </span>
                          </td>
                          <td>
                            <ul className="fulfillment-items">
                              {fulfillment.items.map(item => (
                                <li key={item.id}>
                                  <span className="fulfillment-item-name">
                                    {item.item.product.name}
                                  </span>
                                  <span className="fulfillment-item-quantity">
                                    {item.quantity.raw}
                                  </span>
                                </li>
                              ))}
                            </ul>

                            <div className="fulfillment-tracker">
                              {fulfillmentTracker(fulfillment)}
                            </div>
                          </td>
                          <td>
                            {fulfillment.status === 'shipped' && (
                              <button
                                className="shipping-tracking-button"
                                onClick={() => {
                                  const token = generateShippingToken(
                                    props.order.id,
                                    fulfillment._id,
                                  );
                                  const url = `/orders/${props.order.id}/shipping-tracking/${fulfillment._id}?token=${token}`;
                                  window.open(url, '_blank');
                                }}
                              >
                                배송조회 &gt;
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          }

          {
            <div className="order-refunds card">
              <div className="card-header">
                <h3 className="card-title">환불 내역</h3>
              </div>
              <div className="card-content">
                <ul className="refund-actions">
                  <li>
                    <Button
                      className="button-tiny request-refund"
                      onClick={() => props.goRefund(props.order.id)}
                    >
                      환불 요청
                    </Button>
                  </li>
                </ul>

                {!props.order.refunds.length && <p className="no-refunds">환불 내역이 없습니다.</p>}

                {!!props.order.refunds.length && (
                  <table className="table order-refund-list">
                    <thead>
                      <tr>
                        <th>환불 상태</th>
                        <th>환불 품목</th>
                        <th>환불 금액</th>
                      </tr>
                    </thead>

                    <tbody>
                      {props.order.refunds.map((refund, index) => (
                        <tr key={refund._id} className="order-refund" data-refund={refund._id}>
                          <td>
                            <span className="tag refund-status" data-status={refund.status}>
                              {refund.status === 'cancelled'
                                ? statusTrans(
                                    camelCase([
                                      'refundStatus',
                                      refund.status,
                                      'by',
                                      refund.cancellation.by,
                                    ]),
                                  )
                                : statusTrans(camelCase(['refundStatus', refund.status]))}
                            </span>
                          </td>
                          <td>
                            <ul className="refund-items">
                              {refund.items.map((item, i) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <li key={`${item.id}-${i}`}>
                                  <span className="refund-item-name">{item.item.product.name}</span>
                                  <span className="refund-item-quantity">{item.quantity.raw}</span>
                                </li>
                              ))}
                            </ul>
                            {refund.status === 'requested' && (
                              <div className="refund-cancellation">
                                {refund.status === 'requested' && (
                                  <div>
                                    <Button
                                      className={cx('button-tiny', {
                                        hidden: props.isRefundCancellation,
                                      })}
                                      onClick={props.changeRefundCancellation}
                                    >
                                      환불 요청 취소
                                    </Button>
                                    <div
                                      className={cx('refund-cancellation-form', {
                                        hidden: !props.isRefundCancellation,
                                      })}
                                    >
                                      <textarea
                                        name="reason"
                                        className="refund-cancellation-reason"
                                        placeholder="취소 사유"
                                        value={props.refundCancellationReason}
                                        onChange={props.changeRefundCancellationReason}
                                      />
                                      <Button
                                        className="button-tiny"
                                        onClick={() => props.onRefundCancellation(refund.id)}
                                      >
                                        환불 요청 취소
                                      </Button>
                                    </div>
                                  </div>
                                )}
                                {refund.status === 'cancelled' && refund.cancellation.reason && (
                                  <span
                                    className="refund-cancellation-reason"
                                    data-balloon-length="large"
                                    data-balloon-pos="up-left"
                                    data-balloon={refund.cancellation.reason}
                                  >
                                    환불 취소 사유
                                    <span className="question-mark"></span>
                                  </span>
                                )}
                              </div>
                            )}
                          </td>
                          <td>
                            <span className="refund-total-label hidden">환불 금액</span>
                            <span
                              className="refund-total"
                              data-balloon-length="large"
                              data-balloon-pos="up-left"
                              data-balloon={refundTotalDetail(refund.total)}
                            >
                              {numberWithCommas(refund.total.price.withTax.raw)}원
                              <span className="question-mark"></span>
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          }
        </div>

      </div>
    </div>
  </div>
);

export default OrderDetailTemplate;



// WEBPACK FOOTER //
// ./src/components/order/OrderDetailTemplate/OrderDetailTemplate.js